import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";

import { useTranslation } from "react-i18next";
import * as authApi from "@/api/auth";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  avatar_url: string | null;
  preferred_language: string;
  preferred_theme: string;
}

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: UserProfile;
}

export interface RegisterData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
}

export interface LoginData {
  email: string;
  password: string;
}

interface AuthContextValue {
  /** The currently authenticated user, or null if not logged in. */
  user: UserProfile | null;
  /** Whether the user is authenticated. */
  isAuthenticated: boolean;
  /** Whether the initial auth check is still in progress. */
  isLoading: boolean;
  /** Log in with email + password. Returns the user profile on success. */
  login: (data: LoginData) => Promise<UserProfile>;
  /** Register a new account. Returns the user profile on success. */
  register: (data: RegisterData) => Promise<UserProfile>;
  /** Log out the current user and clear tokens. */
  logout: () => Promise<void>;
  /** Silently refresh the access token using the stored refresh token. */
  refreshToken: () => Promise<boolean>;
  /** Update the local user state (e.g. after a profile edit). */
  setUser: React.Dispatch<React.SetStateAction<UserProfile | null>>;
}

// ─── Token helpers ───────────────────────────────────────────────────────────

const ACCESS_TOKEN_KEY = "katalog-access-token";
const REFRESH_TOKEN_KEY = "katalog-refresh-token";

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

function storeTokens(accessToken: string, refreshToken: string) {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

function clearTokens() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

// ─── Context ─────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Custom hook to consume the auth context.
 * Must be used inside an `<AuthProvider>`.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}

// ─── Provider ────────────────────────────────────────────────────────────────

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { i18n } = useTranslation();

  const isAuthenticated = user !== null;

  // ── Bootstrap: check for existing tokens on mount ──────────────────────

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      const accessToken = getAccessToken();
      const refreshTokenValue = getRefreshToken();

      if (!accessToken) {
        setIsLoading(false);
        return;
      }

      try {
        // Try to load the user profile with the stored access token
        const profile = await authApi.getProfile();
        if (!cancelled) {
          setUser(profile);
          // Sync i18n language with user preference
          if (
            profile.preferred_language &&
            profile.preferred_language !== i18n.language
          ) {
            i18n.changeLanguage(profile.preferred_language);
          }
        }
      } catch {
        // Access token might be expired — try refreshing
        if (refreshTokenValue) {
          try {
            const response = await authApi.refresh(refreshTokenValue);
            storeTokens(response.access_token, response.refresh_token);
            if (!cancelled) {
              setUser(response.user);
            }
          } catch {
            // Refresh also failed — clear everything
            clearTokens();
          }
        } else {
          clearTokens();
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    bootstrap();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Actions ────────────────────────────────────────────────────────────

  const login = useCallback(
    async (data: LoginData): Promise<UserProfile> => {
      const response: AuthResponse = await authApi.login(data);
      storeTokens(response.access_token, response.refresh_token);
      setUser(response.user);

      // Sync language preference
      if (
        response.user.preferred_language &&
        response.user.preferred_language !== i18n.language
      ) {
        i18n.changeLanguage(response.user.preferred_language);
      }

      return response.user;
    },
    [i18n],
  );

  const register = useCallback(
    async (data: RegisterData): Promise<UserProfile> => {
      const response: AuthResponse = await authApi.register(data);
      storeTokens(response.access_token, response.refresh_token);
      setUser(response.user);

      if (
        response.user.preferred_language &&
        response.user.preferred_language !== i18n.language
      ) {
        i18n.changeLanguage(response.user.preferred_language);
      }

      return response.user;
    },
    [i18n],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore errors during logout — we clear local state regardless
    } finally {
      clearTokens();
      setUser(null);
    }
  }, []);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    const rt = getRefreshToken();
    if (!rt) return false;

    try {
      const response = await authApi.refresh(rt);
      storeTokens(response.access_token, response.refresh_token);
      setUser(response.user);
      return true;
    } catch {
      clearTokens();
      setUser(null);
      return false;
    }
  }, []);

  // ── Memoised value ─────────────────────────────────────────────────────

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      login,
      register,
      logout,
      refreshToken,
      setUser,
    }),
    [user, isAuthenticated, isLoading, login, register, logout, refreshToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthContext;
